import { useEffect, useMemo, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { CloudSunIcon } from 'lucide-react';
import type { FcAdminMutationOperation, FcAdminMutationRequest, FcAdminReadOperation, FcAdminTarget } from '@shared/fcAdminApiTypes';
import { PageHeader } from '@/components/page-header';
import FcPageFrame from '@/components/FcPageFrame';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button, buttonVariants } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatFcAdminNotice, useFcAdminAccess, useFcAdminAction, useFcAdminQuery } from '@/hooks/fcAdmin';
import FcAdminStatusBar from './FcAdminStatusBar';
import { buildActionCharacterOptions, formatActionConfirmationTarget, type ActionCharacterOption } from './fcAdminActionTargetModel';

type Field = {
    key: string;
    label: string;
    type?: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'search';
    required?: boolean;
    min?: number;
    max?: number;
    options?: { value: string; label: string }[];
    dynamicOptions?: 'vipTiers' | 'licenseTypes' | 'groupOptions' | 'uidWeapons' | 'items' | 'weapons' | 'vehicles' | 'jobs' | 'jobGrades';
    visibleWhen?: { key: string; values: string[] };
};

export type ActionDefinition = {
    operation: FcAdminMutationOperation;
    label: string;
    description: string;
    targetRequired?: boolean;
    fields: Field[];
    optionsOperation?: Extract<FcAdminReadOperation, 'vip.read' | 'licenses.read'>;
};

export type ActionCatalog = {
    items?: { name: string; label: string }[];
    weapons?: { name: string; label: string; ammoMin: number; ammoMax: number; defaultAmmo: number }[];
    vehicles?: { model: string; label: string; type: 'car' | 'bike' | 'boat' | 'airplane' }[];
    jobs?: { name: string; label: string; grades: { grade: number; label: string }[] }[];
};

const offlinePersistent = new Set<FcAdminMutationOperation>([
    'moderation.ban',
    'moderation.warn',
    'moderation.note',
    'player.job',
    'player.group',
    'player.money',
    'player.item',
    'player.weapon',
    'player.coins',
    'vehicle.give',
    'vehicle.impound',
    'vehicle.delete',
    'vip.update',
    'licenses.update',
]);

const actionRecord = (value: unknown) => value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

const actionRecords = (value: unknown) => Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
    : [];

const actionValue = (value: unknown, fallback = 'Nicht gesetzt') => {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value === 'boolean') return value ? 'Aktiv' : 'Inaktiv';
    return fallback;
};

export const fcActionDefinitions: ActionDefinition[] = [
    { operation: 'moderation.ban', label: 'Spieler bannen', description: 'Legt einen FC-Ban an und beendet eine aktive Sitzung.', targetRequired: true, fields: [{ key: 'reason', label: 'Grund', required: true }, { key: 'hours', label: 'Dauer in Stunden, 0 = permanent', type: 'number', required: true }] },
    { operation: 'moderation.warn', label: 'Spieler verwarnen', description: 'Speichert eine Verwarnung in der FC-Spielerakte.', targetRequired: true, fields: [{ key: 'reason', label: 'Grund', required: true }] },
    { operation: 'moderation.note', label: 'Notiz anlegen', description: 'Ergänzt eine interne Notiz in der Spielerakte.', targetRequired: true, fields: [{ key: 'note', label: 'Notiz', required: true }] },
    { operation: 'moderation.revoke', label: 'Akteneintrag widerrufen', description: 'Widerruft den ausgewählten aktiven Akteneintrag.', fields: [{ key: 'kind', label: 'Typ', required: true }, { key: 'id', label: 'Eintrag', required: true }, { key: 'banId', label: 'Ban', required: false }, { key: 'sourceSystem', label: 'Quellsystem' }, { key: 'sourceId', label: 'Quell-ID' }, { key: 'sourceScope', label: 'Quellbereich' }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.kick', label: 'Spieler kicken', description: 'Trennt ausschließlich die aktuell geprüfte Sitzung.', targetRequired: true, fields: [{ key: 'reason', label: 'Grund', required: true }] },
    { operation: 'player.freeze', label: 'Freeze umschalten', description: 'Ändert den serverautorisierten Freeze-Zustand.', targetRequired: true, fields: [{ key: 'enabled', label: 'Zustand', type: 'boolean', required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.heal', label: 'Spieler heilen', description: 'Nutzt den zentralen ESX-Heilpfad.', targetRequired: true, fields: [{ key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.revive', label: 'Spieler wiederbeleben', description: 'Nutzt den zentralen ESX-Todeskern.', targetRequired: true, fields: [{ key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.status', label: 'Hunger und Durst setzen', description: 'Setzt den Status über fc_food für die geprüfte Sitzung.', targetRequired: true, fields: [{ key: 'hunger', label: 'Hunger', type: 'number', min: 0, max: 100, required: true }, { key: 'thirst', label: 'Durst', type: 'number', min: 0, max: 100, required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.dimension', label: 'Dimension setzen', description: 'Setzt den Routing Bucket nach erneuter Zielprüfung.', targetRequired: true, fields: [{ key: 'dimension', label: 'Dimension', type: 'number', min: 0, max: 10000, required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.job', label: 'Job setzen', description: 'Ändert Job und Rang serverseitig.', targetRequired: true, fields: [{ key: 'job', label: 'Job', type: 'search', dynamicOptions: 'jobs', required: true }, { key: 'grade', label: 'Rang', type: 'select', dynamicOptions: 'jobGrades', required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.group', label: 'Gruppe setzen', description: 'Ändert die ESX-Gruppe innerhalb der eigenen FC-Hierarchie.', targetRequired: true, fields: [{ key: 'group', label: 'Gruppe', type: 'select', required: true, dynamicOptions: 'groupOptions' }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.money', label: 'Geld ändern', description: 'Führt eine autorisierte und geprüfte Kontobuchung aus.', targetRequired: true, fields: [{ key: 'account', label: 'Konto', type: 'select', required: true, options: [{ value: 'money', label: 'Bargeld' }, { value: 'bank', label: 'Bank' }, { value: 'black_money', label: 'Schwarzgeld' }] }, { key: 'mode', label: 'Buchung', type: 'select', required: true, options: [{ value: 'add', label: 'Hinzufügen' }, { value: 'remove', label: 'Entfernen' }, { value: 'set', label: 'Fest setzen' }] }, { key: 'amount', label: 'Betrag', type: 'number', min: 0, max: 100000000, required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.item', label: 'Inventar ändern', description: 'Ändert normale Items nach Gewichts- und Bestandsprüfung.', targetRequired: true, fields: [{ key: 'item', label: 'Item', type: 'search', dynamicOptions: 'items', required: true }, { key: 'mode', label: 'Änderung', type: 'select', required: true, options: [{ value: 'add', label: 'Hinzufügen' }, { value: 'remove', label: 'Entfernen' }, { value: 'set', label: 'Fest setzen' }] }, { key: 'amount', label: 'Menge', type: 'number', min: 0, max: 1000, required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.weapon', label: 'UID-Waffe ändern', description: 'Fügt eine Waffeninstanz exakt hinzu oder entfernt sie revisionssicher.', targetRequired: true, fields: [{ key: 'mode', label: 'Änderung', type: 'select', required: true, options: [{ value: 'add', label: 'Hinzufügen' }, { value: 'remove', label: 'Entfernen' }] }, { key: 'weapon', label: 'Waffe', type: 'search', dynamicOptions: 'weapons', required: true, visibleWhen: { key: 'mode', values: ['add'] } }, { key: 'ammo', label: 'Munition', type: 'number', min: 0, max: 2000, visibleWhen: { key: 'mode', values: ['add'] } }, { key: 'tintIndex', label: 'Farbvariante', type: 'number', min: 0, visibleWhen: { key: 'mode', values: ['add'] } }, { key: 'uid', label: 'Waffeninstanz', type: 'select', dynamicOptions: 'uidWeapons', required: true, visibleWhen: { key: 'mode', values: ['remove'] } }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'player.coins', label: 'Coins ändern', description: 'Bucht Coins mit vollständigem Audit.', targetRequired: true, fields: [{ key: 'mode', label: 'Buchung', type: 'select', required: true, options: [{ value: 'add', label: 'Hinzufügen' }, { value: 'remove', label: 'Entfernen' }, { value: 'set', label: 'Fest setzen' }] }, { key: 'amount', label: 'Betrag', type: 'number', min: 0, max: 1000000, required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'vehicle.give', label: 'Fahrzeug geben', description: 'Vergibt ein Fahrzeug über den autoritativen fc_garage-Pfad.', targetRequired: true, fields: [{ key: 'model', label: 'Modell', type: 'search', dynamicOptions: 'vehicles', required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'vehicle.impound', label: 'Fahrzeug verwahren', description: 'Verwahrt ein eindeutig geprüftes Online- oder Offline-Fahrzeug.', targetRequired: true, fields: [{ key: 'plate', label: 'Kennzeichen', required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'vehicle.delete', label: 'Fahrzeug endgültig löschen', description: 'Löscht das eindeutig geprüfte Fahrzeug und seine Kennzeichen-Daten.', targetRequired: true, fields: [{ key: 'plate', label: 'Kennzeichen', required: true }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'jail.apply', label: 'Admin Jail', description: 'Inhaftiert die geprüfte Zielsession.', targetRequired: true, fields: [{ key: 'durationMinutes', label: 'Dauer in Minuten', type: 'number', min: 1, max: 1440, required: true }, { key: 'reason', label: 'Grund', required: true }] },
    { operation: 'jail.release', label: 'Admin Jail beenden', description: 'Beendet einen aktiven Admin-Jail-Fall.', targetRequired: true, fields: [{ key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'support.assign', label: 'Support-Ticket übernehmen', description: 'Weist ein offenes oder bereits betreutes Ticket dem verknüpften Webkonto zu.', fields: [{ key: 'ticketId', label: 'Ticket-ID', type: 'number', min: 1, required: true }] },
    { operation: 'support.close', label: 'Support-Ticket schließen', description: 'Schließt ein Support-Ticket autoritativ und informiert den Spieler, falls er online ist.', fields: [{ key: 'ticketId', label: 'Ticket-ID', type: 'number', min: 1, required: true }, { key: 'reason', label: 'Abschlussgrund' }] },
    { operation: 'entry.approve', label: 'Einreise genehmigen', description: 'Genehmigt die Einreise nur für die unmittelbar erneut geprüfte Spielersitzung.', targetRequired: true, fields: [] },
    { operation: 'entry.reject', label: 'Einreise ablehnen', description: 'Lehnt die Einreise ab und setzt eine zeitlich begrenzte Einreisesperre.', targetRequired: true, fields: [{ key: 'minutes', label: 'Sperrdauer in Minuten', type: 'number', min: 5, max: 4320, required: true }, { key: 'reason', label: 'Grund', required: true }] },
    { operation: 'vip.update', label: 'VIP verwalten', description: 'Vergibt oder entfernt VIP über fc_vip.', targetRequired: true, optionsOperation: 'vip.read', fields: [{ key: 'action', label: 'Aktion', type: 'select', required: true, options: [{ value: 'grant', label: 'VIP vergeben' }, { value: 'clear', label: 'VIP entfernen' }] }, { key: 'tier', label: 'VIP-Stufe', type: 'select', dynamicOptions: 'vipTiers', required: true, visibleWhen: { key: 'action', values: ['grant'] } }, { key: 'days', label: 'Laufzeit in Tagen', type: 'number', min: 1, required: true, visibleWhen: { key: 'action', values: ['grant'] } }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'licenses.update', label: 'Lizenzen verwalten', description: 'Vergibt oder entzieht verwaltete FC-Lizenzen und pflegt Behindertenausweise.', targetRequired: true, optionsOperation: 'licenses.read', fields: [{ key: 'action', label: 'Aktion', type: 'select', required: true, options: [{ value: 'give', label: 'Lizenz vergeben' }, { value: 'revoke', label: 'Lizenz entziehen' }, { value: 'revokeAll', label: 'Alle verwalteten entziehen' }, { value: 'setDisability', label: 'Behindertenausweis speichern' }] }, { key: 'licenseType', label: 'Lizenztyp', type: 'select', dynamicOptions: 'licenseTypes', required: true, visibleWhen: { key: 'action', values: ['give', 'revoke'] } }, { key: 'disabilityKind', label: 'Art der Behinderung', required: true, visibleWhen: { key: 'action', values: ['setDisability'] } }, { key: 'disabilityDegree', label: 'Grad der Behinderung', type: 'number', min: 1, max: 100, required: true, visibleWhen: { key: 'action', values: ['setDisability'] } }, { key: 'disabilityMarkers', label: 'Merkzeichen', visibleWhen: { key: 'action', values: ['setDisability'] } }, { key: 'disabilityValidUntil', label: 'Gültig bis', type: 'date', visibleWhen: { key: 'action', values: ['setDisability'] } }, { key: 'disabilityNotes', label: 'Hinweise', visibleWhen: { key: 'action', values: ['setDisability'] } }, { key: 'reason', label: 'Begründung', required: true }] },
    { operation: 'world.announcement', label: 'Ankündigung', description: 'Sendet eine serverweite FC-Ankündigung.', fields: [{ key: 'title', label: 'Titel' }, { key: 'message', label: 'Nachricht', required: true }, { key: 'duration', label: 'Dauer in Millisekunden', type: 'number', min: 3000, max: 60000 }, { key: 'announceType', label: 'Darstellung', type: 'select', options: ['server', 'event', 'info', 'success', 'warning', 'alert', 'restart', 'job'].map((value) => ({ value, label: value })) }] },
    { operation: 'world.weather', label: 'Wetter setzen', description: 'Ändert das serverautorisierte Wetter.', fields: [{ key: 'weather', label: 'Wettertyp', type: 'select', required: true, options: ['CLEAR', 'EXTRASUNNY', 'CLOUDS', 'OVERCAST', 'RAIN', 'CLEARING', 'THUNDER', 'SMOG', 'FOGGY', 'XMAS', 'SNOW', 'SNOWLIGHT', 'BLIZZARD', 'NEUTRAL'].map((value) => ({ value, label: value })) }] },
    { operation: 'world.time', label: 'Uhrzeit setzen', description: 'Ändert die serverautorisierte Uhrzeit.', fields: [{ key: 'hour', label: 'Stunde', type: 'number', min: 0, max: 23, required: true }, { key: 'minute', label: 'Minute', type: 'number', min: 0, max: 59, required: true }] },
];

export function ActionForm({ definition, disabled, groupOptions, lockedTarget, currentValues = {}, catalog = {}, presetFields = {}, hiddenFields = [], submitLabel, onSuccess }: {
    definition: ActionDefinition;
    disabled: boolean;
    groupOptions: { value: string; label: string }[];
    lockedTarget?: FcAdminTarget;
    currentValues?: Record<string, unknown>;
    catalog?: ActionCatalog;
    presetFields?: Record<string, string | number | boolean>;
    hiddenFields?: string[];
    submitLabel?: string;
    onSuccess?: () => void;
}) {
    const { accessRevision } = useFcAdminAccess();
    const execute = useFcAdminAction(definition.operation);
    const playerLookup = useFcAdminQuery('players.directory');
    const templatesQuery = useFcAdminQuery('moderation.templates');
    const { run: loadOptions } = useFcAdminQuery(definition.optionsOperation ?? 'dashboard.read');
    const loadOptionsRef = useRef(loadOptions);
    loadOptionsRef.current = loadOptions;
    const [publicId, setPublicId] = useState('');
    const initialFields = () => Object.fromEntries(Object.entries(presetFields).map(([key, value]) => [key, String(value)]));
    const [fields, setFields] = useState<Record<string, string>>(initialFields);
    const [dynamicOptions, setDynamicOptions] = useState<Record<string, { value: string; label: string }[]>>({});
    const [result, setResult] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [pending, setPending] = useState<FcAdminMutationRequest>();
    const [targetSelection, setTargetSelection] = useState('');
    const [targetOptions, setTargetOptions] = useState<ActionCharacterOption[]>([]);
    const [pendingCharacterName, setPendingCharacterName] = useState<string>();
    const [templates, setTemplates] = useState<{ id: string; label: string; reason: string; hours: number }[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');

    const confirmationPreview = useMemo(() => {
        if (!pending) return undefined;
        const payload = pending.payload ?? {};
        const mode = String(payload.mode ?? 'set');
        const amount = Number(payload.amount ?? 0);
        const applyAmount = (before: number) => mode === 'add'
            ? before + amount
            : mode === 'remove' ? Math.max(0, before - amount) : amount;
        if (definition.operation === 'player.money') {
            const account = String(payload.account ?? 'money');
            const before = Number(actionRecord(currentValues.accounts)[account] ?? 0);
            return { before: `${account}: ${before}`, after: `${account}: ${applyAmount(before)}` };
        }
        if (definition.operation === 'player.coins') {
            const before = Number(currentValues.coins ?? actionRecord(currentValues.accounts).coins ?? 0);
            return { before: `Coins: ${before}`, after: `Coins: ${applyAmount(before)}` };
        }
        if (definition.operation === 'player.item') {
            const itemName = String(payload.item ?? 'Item');
            const item = actionRecords(currentValues.inventory).find((entry) => entry.name === itemName || entry.label === itemName);
            const before = Number(item?.count ?? item?.amount ?? 0);
            return { before: `${itemName}: ${before}`, after: `${itemName}: ${applyAmount(before)}` };
        }
        if (definition.operation === 'player.weapon') {
            const weapons = actionRecords(currentValues.weapons ?? currentValues.uidWeapons);
            if (payload.mode === 'remove') {
                const weapon = weapons.find((entry) => String(entry.uid) === String(payload.uid));
                return {
                    before: `${actionValue(weapon?.label ?? weapon?.name, 'UID-Waffe')} · UID ${actionValue(payload.uid)}`,
                    after: 'Waffeninstanz entfernt',
                };
            }
            return {
                before: `${weapons.length} UID-Waffen`,
                after: `${weapons.length + 1} UID-Waffen · neu: ${actionValue(payload.weapon)}`,
            };
        }
        if (definition.operation === 'player.job') {
            const profile = actionRecord(currentValues.profile);
            return {
                before: `${actionValue(profile.jobLabel ?? profile.jobName ?? profile.job ?? currentValues.jobLabel ?? currentValues.jobName ?? currentValues.job)} · Rang ${actionValue(profile.jobGrade ?? currentValues.jobGrade, '–')}`,
                after: `${actionValue(payload.job)} · Rang ${actionValue(payload.grade, '–')}`,
            };
        }
        if (definition.operation === 'player.group') {
            const profile = actionRecord(currentValues.profile);
            return { before: actionValue(profile.groupLabel ?? profile.group ?? currentValues.groupLabel ?? currentValues.group), after: actionValue(payload.group) };
        }
        if (definition.operation === 'vip.update') {
            const vip = actionRecord(currentValues.vip);
            return {
                before: actionValue(vip.label ?? vip.tier, 'Kein VIP'),
                after: payload.action === 'clear' ? 'Kein VIP' : `${actionValue(payload.tier)} · ${actionValue(payload.days, '–')} Tage`,
            };
        }
        if (definition.operation === 'licenses.update') {
            const count = actionRecords(currentValues.licenses).length;
            return { before: `${count} aktive Zugänge`, after: `${actionValue(payload.action)} · ${actionValue(payload.licenseType, 'alle verwalteten Zugänge')}` };
        }
        if (definition.operation === 'vehicle.give') {
            const count = actionRecords(currentValues.vehicles).length;
            return { before: `${count} Fahrzeuge`, after: `${count + 1} Fahrzeuge · neu: ${actionValue(payload.model)}` };
        }
        if (definition.operation === 'vehicle.impound') {
            const vehicle = actionRecords(currentValues.vehicles).find((entry) => String(entry.plate).trim().toUpperCase() === String(payload.plate).trim().toUpperCase());
            return {
                before: vehicle?.impounded === true ? `${actionValue(payload.plate)} · bereits verwahrt` : `${actionValue(payload.plate)} · nicht verwahrt`,
                after: `${actionValue(payload.plate)} · verwahrt`,
            };
        }
        if (definition.operation === 'vehicle.delete') {
            const vehicle = actionRecords(currentValues.vehicles).find((entry) => String(entry.plate).trim().toUpperCase() === String(payload.plate).trim().toUpperCase());
            return {
                before: `${actionValue(vehicle?.displayName ?? vehicle?.vehiclename ?? vehicle?.model, 'Fahrzeug')} · ${actionValue(payload.plate)}`,
                after: 'Fahrzeug und verknüpfte Kennzeichen-Daten endgültig gelöscht',
            };
        }
        if (definition.operation === 'moderation.revoke') {
            return { before: `${actionValue(payload.kind)} · aktiv`, after: `Widerrufen · ${actionValue(payload.reason)}` };
        }
        if (definition.operation === 'moderation.ban' || definition.operation === 'moderation.warn' || definition.operation === 'moderation.note') {
            return { before: 'Kein neuer Akteneintrag', after: actionValue(payload.reason ?? payload.note, definition.label) };
        }
        return {
            before: 'Wird serverseitig direkt vor dem Commit erneut geprüft',
            after: Object.values(payload).map((value) => actionValue(value)).join(' · ') || definition.label,
        };
    }, [currentValues, definition.label, definition.operation, pending]);

    const destructiveConfirmation = useMemo(() => {
        const payload = pending?.payload ?? {};
        if (definition.operation === 'vehicle.delete' || definition.operation === 'moderation.revoke'
            || definition.operation === 'player.kick' || definition.operation === 'moderation.ban') return true;
        if (definition.operation === 'player.weapon') return payload.mode === 'remove';
        if (definition.operation === 'vip.update') return payload.action === 'clear';
        if (definition.operation === 'licenses.update') return payload.action === 'revoke' || payload.action === 'revokeAll';
        if (definition.operation === 'player.money' || definition.operation === 'player.item' || definition.operation === 'player.coins') {
            return payload.mode === 'remove';
        }
        return false;
    }, [definition.operation, pending]);

    useEffect(() => {
        setPublicId('');
        setFields(initialFields());
        setDynamicOptions({});
        setResult(undefined);
        setBusy(false);
        setPending(undefined);
        setTargetSelection('');
        setTargetOptions([]);
        setPendingCharacterName(undefined);
        setTemplates([]);
        setSelectedTemplate('');
    }, [accessRevision]);

    useEffect(() => {
        if (!disabled) return;
        setBusy(false);
        setPending(undefined);
    }, [disabled]);

    useEffect(() => {
        if (!definition.optionsOperation || disabled) return;
        void loadOptionsRef.current().then((response) => {
            if (!response?.ok) return;
            if (definition.optionsOperation === 'vip.read') {
                setDynamicOptions({ vipTiers: response.data.items.flatMap((entry) => (
                    typeof entry.value === 'string'
                        ? [{ value: entry.value, label: typeof entry.label === 'string' ? entry.label : entry.value }]
                        : []
                )) });
            } else {
                setDynamicOptions({ licenseTypes: response.data.items.flatMap((entry) => (
                    typeof entry.type === 'string'
                        ? [{ value: entry.type, label: typeof entry.label === 'string' ? entry.label : entry.type }]
                        : []
                )) });
            }
        });
    }, [definition.optionsOperation, disabled]);

    useEffect(() => {
        if (definition.operation !== 'moderation.ban' || disabled) return;
        void templatesQuery.run({ limit: 100 }).then((response) => {
            if (!response?.ok) return;
            setTemplates(response.data.items.flatMap((item) => {
                const id = item.id;
                const label = typeof item.label === 'string' ? item.label.trim() : '';
                const reason = typeof item.reason === 'string' ? item.reason.trim() : '';
                const hours = Number(item.durationHours ?? item.duration_hours ?? 0);
                return id !== undefined && label && reason && Number.isFinite(hours)
                    ? [{ id: String(id), label, reason, hours: Math.max(0, Math.floor(hours)) }]
                    : [];
            }));
        });
    }, [definition.operation, disabled, templatesQuery.run]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (disabled) {
            setResult('Der Gameserver oder fc_admin ist offline. Es wurde nichts vorgemerkt.');
            return;
        }
        setBusy(true);
        setResult(undefined);
        const applicableFields = definition.fields.filter((field) => (
            !field.visibleWhen || field.visibleWhen.values.includes(fields[field.visibleWhen.key] ?? '')
        ));
        const missing = applicableFields.find((field) => field.required && !fields[field.key]?.trim());
        if (missing) {
            setResult(`${missing.label} ist erforderlich.`);
            setBusy(false);
            return;
        }
        const invalidCatalogField = applicableFields.find((field) => {
            if (field.type !== 'search' || hiddenFields.includes(field.key)) return false;
            const value = fields[field.key]?.trim();
            if (!value) return false;
            if (field.dynamicOptions === 'items') return !catalog.items?.some((entry) => entry.name === value);
            if (field.dynamicOptions === 'weapons') return !catalog.weapons?.some((entry) => entry.name === value);
            if (field.dynamicOptions === 'vehicles') return !catalog.vehicles?.some((entry) => entry.model === value);
            if (field.dynamicOptions === 'jobs') return !catalog.jobs?.some((entry) => entry.name === value);
            return false;
        });
        if (invalidCatalogField) {
            setResult(`${invalidCatalogField.label} muss aus dem aktuellen Serverkatalog gewählt werden.`);
            setBusy(false);
            return;
        }
        const payload: Record<string, unknown> = {};
        for (const field of applicableFields) {
            const value = fields[field.key]?.trim();
            if (!value) continue;
            if (field.type === 'number') payload[field.key] = Number(value);
            else if (field.type === 'boolean') payload[field.key] = value === 'true';
            else payload[field.key] = value;
        }
        if (definition.operation === 'vehicle.give') {
            const vehicle = catalog.vehicles?.find((entry) => entry.model === payload.model);
            if (!vehicle) {
                setResult('Modell muss aus dem aktuellen Serverkatalog gewählt werden.');
                setBusy(false);
                return;
            }
            payload.vehicleType = vehicle.type;
        }
        if (definition.operation === 'licenses.update' && payload.action === 'setDisability') {
            payload.licenseType = 'disability';
            payload.details = {
                kind: payload.disabilityKind,
                degree: payload.disabilityDegree,
                markers: payload.disabilityMarkers ?? '',
                validUntil: payload.disabilityValidUntil ?? '',
                notes: payload.disabilityNotes ?? '',
            };
            delete payload.disabilityKind;
            delete payload.disabilityDegree;
            delete payload.disabilityMarkers;
            delete payload.disabilityValidUntil;
            delete payload.disabilityNotes;
        }
        if (definition.operation === 'player.weapon' && payload.mode === 'remove') {
            const weapon = actionRecords(currentValues.weapons ?? currentValues.uidWeapons)
                .find((entry) => String(entry.uid) === String(payload.uid));
            const revision = Number(weapon?.projectionRevision ?? weapon?.revision);
            if (!Number.isSafeInteger(revision) || revision < 0) {
                setResult('Die Waffeninstanz besitzt keinen aktuellen Revisionsstand. Bitte die Akte neu laden.');
                setBusy(false);
                return;
            }
            payload.expectedRevision = revision;
        }
        let target = lockedTarget;
        if (definition.targetRequired) {
            if (lockedTarget) {
                setPendingCharacterName(undefined);
                setPending({
                    payload,
                    target: lockedTarget,
                    idempotencyKey: `${definition.operation}:${nanoid()}`,
                });
                setBusy(false);
                return;
            }
            if (publicId.trim() === '') {
                setResult('Die permanente FC-ID ist erforderlich.');
                setBusy(false);
                return;
            }
            const requestedId = Number(publicId);
            if (!Number.isInteger(requestedId) || requestedId < 0) {
                setResult('Die permanente FC-ID ist ungültig.');
                setBusy(false);
                return;
            }
            const lookup = await playerLookup.run({ filters: { search: String(requestedId), status: 'all' }, limit: 25 });
            if (!lookup.ok) {
                setResult(formatFcAdminNotice(lookup));
                setBusy(false);
                return;
            }
            const matching = lookup.data.items.filter((item) => Number(item.publicId ?? item.playerId) === requestedId);
            const options = buildActionCharacterOptions(matching, requestedId, offlinePersistent.has(definition.operation));
            if (!options.length) {
                setResult(offlinePersistent.has(definition.operation)
                    ? 'Für diese FC-ID wurde keine eindeutig referenzierbare Charakterakte gefunden.'
                    : 'Diese FC-ID ist nicht online oder die Sitzung hat sich geändert.');
                setTargetOptions([]);
                setTargetSelection('');
                setBusy(false);
                return;
            }
            setTargetOptions(options);
            const selected = options.length === 1
                ? options[0]
                : options.find((option) => option.value === targetSelection);
            if (!selected) {
                setResult('Bitte den betroffenen Charakter auswählen und die Aktion erneut prüfen.');
                setBusy(false);
                return;
            }
            target = selected.target;
            setPendingCharacterName(selected.name);
        }
        setPending({
            payload,
            target,
            idempotencyKey: `${definition.operation}:${nanoid()}`,
        });
        setBusy(false);
    };

    const confirm = async () => {
        if (!pending) return;
        setBusy(true);
        setResult(undefined);
        try {
            const response = await execute(pending);
            if (response) {
                setResult(response.ok ? `Erfolgreich · ${response.correlationId}` : formatFcAdminNotice(response));
                if (response.ok) onSuccess?.();
                if (response.ok || (response.code !== 'bridge_timeout' && response.code !== 'broker_timeout')) setPending(undefined);
            } else {
                setResult('Die Antwort ist ausgeblieben. Ein erneuter Versuch verwendet denselben Idempotenzschlüssel.');
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="space-y-4" onSubmit={submit}>
            {definition.targetRequired && !lockedTarget && (
                <div className="space-y-1.5">
                    <Label htmlFor={`fc-${definition.operation}-public-id`}>Permanente FC-ID</Label>
                    <Input id={`fc-${definition.operation}-public-id`} type="number" min="0" required disabled={disabled || busy} value={publicId} onChange={(event) => {
                        setPublicId(event.target.value);
                        setTargetOptions([]);
                        setTargetSelection('');
                        setPendingCharacterName(undefined);
                    }} />
                </div>
            )}
            {targetOptions.length > 1 && (
                <div className="space-y-1.5">
                    <Label htmlFor={`fc-${definition.operation}-character`}>Charakter</Label>
                    <Select value={targetSelection} onValueChange={setTargetSelection} disabled={disabled || busy}>
                        <SelectTrigger id={`fc-${definition.operation}-character`}><SelectValue placeholder="Charakter auswählen" /></SelectTrigger>
                        <SelectContent>
                            {targetOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            )}
            {definition.operation === 'moderation.ban' && templates.length > 0 && (
                <div className="space-y-1.5">
                    <Label htmlFor="fc-moderation-ban-template">Ban-Vorlage</Label>
                    <Select value={selectedTemplate} onValueChange={(value) => {
                        setSelectedTemplate(value);
                        const template = templates.find((entry) => entry.id === value);
                        if (template) setFields((current) => ({ ...current, reason: template.reason, hours: String(template.hours) }));
                    }} disabled={disabled || busy}>
                        <SelectTrigger id="fc-moderation-ban-template"><SelectValue placeholder="Optional eine Vorlage wählen" /></SelectTrigger>
                        <SelectContent>{templates.map((template) => <SelectItem key={template.id} value={template.id}>{template.label} · {template.hours > 0 ? `${template.hours} Std.` : 'Permanent'}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            )}
            {definition.fields.filter((field) => (
                !hiddenFields.includes(field.key)
                && (!field.visibleWhen || field.visibleWhen.values.includes(fields[field.visibleWhen.key] ?? ''))
            )).map((field) => {
                let options = field.options ?? (field.dynamicOptions === 'groupOptions'
                    ? groupOptions
                    : field.dynamicOptions === 'uidWeapons'
                        ? actionRecords(currentValues.weapons ?? currentValues.uidWeapons).map((weapon) => ({
                            value: String(weapon.uid),
                            label: `${actionValue(weapon.label ?? weapon.name, 'UID-Waffe')} · UID ${actionValue(weapon.uid)}`,
                        }))
                        : field.dynamicOptions === 'items'
                            ? catalog.items?.map((entry) => ({ value: entry.name, label: `${entry.label} · ${entry.name}` }))
                            : field.dynamicOptions === 'weapons'
                                ? catalog.weapons?.map((entry) => ({ value: entry.name, label: `${entry.label} · ${entry.name}` }))
                                : field.dynamicOptions === 'vehicles'
                                    ? catalog.vehicles?.map((entry) => ({ value: entry.model, label: `${entry.label} · ${entry.model}` }))
                                    : field.dynamicOptions === 'jobs'
                                        ? catalog.jobs?.map((entry) => ({ value: entry.name, label: `${entry.label} · ${entry.name}` }))
                                        : field.dynamicOptions === 'jobGrades'
                                            ? catalog.jobs?.find((entry) => entry.name === fields.job)?.grades.map((grade) => ({ value: String(grade.grade), label: `${grade.grade} · ${grade.label}` }))
                                            : field.dynamicOptions ? dynamicOptions[field.dynamicOptions] : undefined);
                if (definition.operation === 'licenses.update' && field.key === 'licenseType' && fields.action === 'give') {
                    options = options?.filter((option) => option.value !== 'disability');
                }
                const selectedWeapon = definition.operation === 'player.weapon' && field.key === 'ammo'
                    ? catalog.weapons?.find((entry) => entry.name === fields.weapon)
                    : undefined;
                const minimum = selectedWeapon?.ammoMin ?? field.min;
                const maximum = selectedWeapon?.ammoMax ?? field.max;
                return (
                    <div className="space-y-1.5" key={field.key}>
                        <Label htmlFor={`fc-${definition.operation}-${field.key}`}>{field.label}</Label>
                        {field.type === 'select' || field.type === 'boolean' ? (
                            <Select
                                disabled={disabled || busy}
                                value={fields[field.key] ?? ''}
                                onValueChange={(value) => setFields((current) => ({ ...current, [field.key]: value }))}
                            >
                                <SelectTrigger id={`fc-${definition.operation}-${field.key}`}><SelectValue placeholder="Auswählen" /></SelectTrigger>
                                <SelectContent>
                                    {(field.type === 'boolean' ? [{ value: 'true', label: 'Aktiv' }, { value: 'false', label: 'Inaktiv' }] : options ?? []).map((option) => (
                                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : field.type === 'search' ? (
                            <>
                                <Input
                                    id={`fc-${definition.operation}-${field.key}`}
                                    list={`fc-${definition.operation}-${field.key}-options`}
                                    required={field.required}
                                    disabled={disabled || busy}
                                    value={fields[field.key] ?? ''}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setFields((current) => {
                                            const next = { ...current, [field.key]: value };
                                            if (definition.operation === 'player.weapon' && field.key === 'weapon') {
                                                const weapon = catalog.weapons?.find((entry) => entry.name === value);
                                                if (weapon) next.ammo = String(weapon.defaultAmmo);
                                            }
                                            if (definition.operation === 'player.job' && field.key === 'job') {
                                                const job = catalog.jobs?.find((entry) => entry.name === value);
                                                next.grade = job?.grades[0] ? String(job.grades[0].grade) : '';
                                            }
                                            return next;
                                        });
                                    }}
                                />
                                <datalist id={`fc-${definition.operation}-${field.key}-options`}>
                                    {(options ?? []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </datalist>
                            </>
                        ) : (
                            <Input
                                id={`fc-${definition.operation}-${field.key}`}
                                type={field.type ?? 'text'}
                                min={minimum}
                                max={maximum}
                                required={field.required}
                                disabled={disabled || busy}
                                value={fields[field.key] ?? ''}
                                onChange={(event) => setFields((current) => ({ ...current, [field.key]: event.target.value }))}
                            />
                        )}
                    </div>
                );
            })}
            <Button type="submit" disabled={disabled || busy}>{busy ? 'Wird ausgeführt …' : submitLabel ?? 'Verbindlich ausführen'}</Button>
            {result && <p className="break-all text-sm text-muted-foreground">{result}</p>}
            <AlertDialog open={!disabled && !!pending} onOpenChange={(open) => !open && !busy && setPending(undefined)}>
                <AlertDialogContent variant={destructiveConfirmation ? 'danger' : 'default'}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{definition.label} verbindlich bestätigen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Ziel: {formatActionConfirmationTarget(pending?.target?.publicId, pendingCharacterName)} · {definition.operation === 'vehicle.delete' ? 'Das Fahrzeug und seine verknüpften Kennzeichen-Daten werden dauerhaft entfernt.' : 'Bitte prüfe Aktion und Ziel vor der Ausführung.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {confirmationPreview ? <div className="fc-action-diff"><div><span>Vorher</span><strong>{confirmationPreview.before}</strong></div><div><span>Nachher</span><strong>{confirmationPreview.after}</strong></div></div> : null}
                    {pending?.payload ? <dl className="fc-action-confirmation">{Object.entries(pending.payload).map(([key, value]) => <div key={key}><dt>{definition.fields.find((field) => field.key === key)?.label ?? key}</dt><dd>{String(value)}</dd></div>)}</dl> : null}
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction className={destructiveConfirmation ? buttonVariants({ variant: 'destructive' }) : undefined} disabled={disabled || busy} onClick={(event) => { event.preventDefault(); void confirm(); }}>
                            {busy ? 'Wird geprüft …' : 'Jetzt ausführen'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </form>
    );
}

export default function FcAdminActionsPage() {
    const { status, capabilities: allowed, mutationReady } = useFcAdminAccess();
    const available = useMemo(() => fcActionDefinitions.filter((action) => action.operation.startsWith('world.') && allowed.has(action.operation)), [allowed]);
    const actionsEnabled = mutationReady;
    const [operation, setOperation] = useState<FcAdminMutationOperation>(available[0]?.operation ?? 'world.announcement');
    const definition = available.find((action) => action.operation === operation) ?? available[0];

    useEffect(() => {
        if (available.length && !available.some((action) => action.operation === operation)) {
            setOperation(available[0].operation);
        }
    }, [available, operation]);

    return (
        <FcPageFrame contentClassName="flex flex-col">
            <PageHeader title="World Settings" icon={<CloudSunIcon />} parentName="FC Admin" parentLink="/">
                <span className="text-sm text-muted-foreground">Serverweite Ankündigung, Wetter und Uhrzeit</span>
            </PageHeader>
            <FcAdminStatusBar />
            <div className="mx-auto grid w-full max-w-5xl gap-4 p-4 md:grid-cols-[20rem_1fr]">
                <Card>
                    <CardHeader><CardTitle>Weltfunktion</CardTitle><CardDescription>Es erscheinen ausschließlich aktuell freigegebene serverweite Funktionen.</CardDescription></CardHeader>
                    <CardContent>
                        <Select disabled={!actionsEnabled} value={operation} onValueChange={(value) => setOperation(value as FcAdminMutationOperation)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {available.map((action) => <SelectItem key={action.operation} value={action.operation}>{action.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {!available.length && <p className="mt-4 text-sm text-muted-foreground">Für dieses Konto sind derzeit keine Webaktionen freigegeben.</p>}
                        {!actionsEnabled && <p className="mt-4 text-sm text-destructive">Der Gameserver oder fc_admin ist offline. Der letzte Lesestand bleibt sichtbar; Aktionen sind vollständig gesperrt und werden nicht vorgemerkt.</p>}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>{definition?.label ?? 'Keine Funktion verfügbar'}</CardTitle><CardDescription>{definition?.description ?? 'Für dieses Konto ist keine Weltfunktion freigegeben.'}</CardDescription></CardHeader>
                    <CardContent>{definition ? (
                        <ActionForm
                            key={definition.operation}
                            definition={definition}
                            disabled={!actionsEnabled}
                            groupOptions={status?.groupOptions ?? []}
                        />
                    ) : null}</CardContent>
                </Card>
            </div>
        </FcPageFrame>
    );
}
